import { useEffect, useRef } from 'react';
import { setup, createActor } from 'xstate';
import { createBrowserInspector } from '@statelyai/inspect';
import { useNavigate, useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import queryString from 'query-string';

import { usePartner } from 'components/app/routes/usePartner';
import { guards } from 'machines/config/guards';
import type { TContext, TEvent } from 'machines/types/machine-config';
import { restoreState } from 'state/reset-state/reset-state-actions';
import { checkMatchPath, stringToBool } from 'utils/misc';
import { pushHistory } from 'state/journey-routes/journey-routes-slice';
import { addQueryString, addHash } from 'machines/utils/misc';
import { useComputeGuards } from 'machines/hooks/useComputeGuards';
import { ReactRenderingError } from 'errors/ReactRenderingError';
import { STORAGE, sessionStorageKeys } from 'config/storage-config';
import { slice as salesOrderSlice } from 'state/sales-order/sales-order-slice';
import { routePath } from 'config/route-config';
import { store } from 'state/store';
import { slice as journeyReoutesSlice } from 'state/journey-routes/journey-routes-slice';

import type { Actor } from 'xstate';

const {
  VITE_ANY_PAGE_LANDING,
  VITE_XSTATE_DEBUG,
  VITE_PATH_PREFIX
} = import.meta.env;

const debug = stringToBool(VITE_XSTATE_DEBUG);

/* istanbul ignore next -- @preserve */
const { inspect } = debug
  ? createBrowserInspector({
      autoStart: true
    })
  : {};

export const useJourneyMachine = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { stateMachineConfig, partnerPath } = usePartner();
  const { pathname } = location;
  const anyPageLanding = stringToBool(VITE_ANY_PAGE_LANDING);
  let anyPageLandingInitialState: string | undefined;
  const computedGuards = useComputeGuards();
  const actor = useRef<Actor<typeof machine> | null>(null);

  /* istanbul ignore else -- @preserve */
  if (anyPageLanding) {
    [anyPageLandingInitialState] =
      Object.entries(stateMachineConfig.states).find(([, value]) => {
        return checkMatchPath(
          pathname,
          (value as { meta?: { path?: string } })?.meta?.path
        );
      }) || [];
  }

  // Define machine ref first so we can use its type
  const machineRef = useRef(
    setup({
      types: {} as {
        context: TContext;
        events: TEvent;
      },
      guards
    }).createMachine({
      ...stateMachineConfig,
      initial: anyPageLandingInitialState ?? stateMachineConfig.initial
    })
  );

  const machine = machineRef.current;

  if (!actor.current) {
    actor.current = createActor(machine, {
      inspect
    });
  }

  useEffect(() => {
    const { search, hash, pathname } = location;

    actor.current?.start();
    actor.current?.send({
      type: 'UPDATE_GUARDS',
      guards: computedGuards
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
    const state = actor.current?.getSnapshot();

    /* istanbul ignore next -- @preserve */
    if (state?.status === 'error') {
      throw new ReactRenderingError({
        error: new Error(String(state?.error)),
        errorInfo: { componentStack: '' }
      });
    }

    // initial path
    const fullPath = addHash(
      addQueryString(
        pathname,
        queryString.parse(search) as Record<string, string>
      ),
      hash
    );
    dispatch(pushHistory(fullPath));

    // handle browser back/forward navigation
    const popstateHandler = () => {
      // In v6, react-router stores user state under window.history.state.usr
      const state = window.history.state?.usr;
      const sessionState = STORAGE.getItem(sessionStorageKeys.STATE);
      const sessionStore = sessionState?.store;
      const isOrderComplete = !!sessionStore?.[salesOrderSlice.name]?.data
        ?.transactionId;
      const landingPath = sessionStore?.[journeyReoutesSlice.name]?.history[0];
      const currentFullPath = window.location.href
        .replace(`${VITE_PATH_PREFIX}`, '')
        .replace(window.location.origin, '');
      const hasReachedLandingPath = currentFullPath === landingPath;

      if (!state) {
        // browser forward navigation
        if (sessionState?.navigationEvent) {
          actor.current?.send(sessionState.navigationEvent);
        }
        dispatch(
          restoreState({
            store: sessionStore
          })
        );

        return;
      }

      // browser back navigation
      if (isOrderComplete && !hasReachedLandingPath) {
        // force user to stay on order complete page until reaching initial path
        navigate(routePath.orderComplete, {
          replace: true,
          state: {
            store: store.getState(),
            snapshot: actor.current?.getSnapshot().toJSON(),
            partnerName: partnerPath
          }
        });
        return;
      }

      if (isOrderComplete && hasReachedLandingPath) {
        // reload app once user went all the way bac to initial path to restart journey
        window.location.reload();
      }

      actor.current?.stop();

      const newActor = createActor(machine, {
        inspect,
        snapshot: state?.snapshot
      });
      newActor.start();
      actor.current = newActor;

      dispatch(restoreState({ store: state?.store }));
    };

    window.addEventListener('popstate', popstateHandler);

    return () => {
      window.removeEventListener('popstate', popstateHandler);
      actor.current?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    actor: actor.current,
    machine
  };
};
